import { useWebWoodContext } from 'context/WebWoodContext'
import { RefObject, useEffect, useRef, useState } from 'react'
import { IMediaItem } from 'types/media'
import Moveable from 'react-moveable'
import { ProcessingEvent } from '@renderforest/webwood-engine'
import store, { dispatch } from 'store'
import projectDataSlice from 'store/features/projectData/slice'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'

export const useLiveSizes = (
  item: IMediaItem,
  moveableRef: RefObject<Moveable | null>
) => {
  const { engine } = useWebWoodContext()
  const isResizingRef = useRef(false)
  const isTextSideResizingRef = useRef(false)

  const [liveSize, setLiveSize] = useState<{
    width: number
    height: number
  } | null>(null)
  // For rotated text, the engine compensates x/y when the bitmap grows so the
  // visible center stays put. liveCoordinates carries those engine-adjusted
  // values through to the DOM frame so it tracks the bitmap in real time.
  const [liveCoordinates, setLiveCoordinates] = useState<{
    x: number
    y: number
  } | null>(null)

  // Drop the live (engine-ahead-of-Redux) overrides whenever the authoritative
  // Redux values catch up — size OR position. Coordinates matter because a
  // pure move/undo/redo changes only x/y: without this, a `liveCoordinates`
  // seeded by an earlier render (e.g. a color/text edit) would stick and mask
  // the restored `item.coordinates`, leaving the Moveable border at the old
  // spot while the engine text moves to the new one.
  useEffect(() => {
    setLiveSize(null)
    setLiveCoordinates(null)
  }, [item.width, item.height, item.coordinates.x, item.coordinates.y])

  useEffect(() => {
    if (liveSize) {
      moveableRef.current?.updateRect()
    }
  }, [liveSize, moveableRef])

  useEffect(() => {
    if (!engine) return
    const handler = ({
      id,
      width,
      height,
      x,
      y
    }: {
      id: string
      width: number
      height: number
      x?: number
      y?: number
    }) => {
      if (id !== item.id) return
      // During text side-resize we want live height updates from the engine
      // (the bitmap reflows and emits new height). For all other resize types
      // the CSS frame is authoritative, so suppress engine-driven size changes.
      if (isResizingRef.current && !isTextSideResizingRef.current) return
      setLiveSize({ width, height })
      // During text side-resize, Moveable's drag.left/top drives x/y and the
      // engine's emitted x/y is stale (Moveable hasn't pushed the new values
      // yet). Skip the coordinate update in that path.
      if (
        !isTextSideResizingRef.current &&
        typeof x === 'number' &&
        typeof y === 'number'
      ) {
        setLiveCoordinates({ x, y })
      }

      // Persist the engine-derived size back to the store so the Moveable
      // frame and autosave track the true rendered size even after `liveSize`
      // resets (it's local state — a frame re-mount on re-select drops it,
      // falling back to the store dims). The engine only auto-persists dims on
      // FIRST render (OBJECT_DIMENSIONS_RESOLVED); every later re-render lands
      // here instead — notably a corner (scale) resize re-wrapping auto-width
      // text at the canvas cap, whose store dims would otherwise stay at the
      // pre-render, uniformly-scaled (wrong-shape) values and leave the frame
      // oversized. Skip while a gesture is active (the resize/drag handlers own
      // the commit) or while editing this text (persistEditingText owns dims),
      // and no-op when the store already matches. Non-undoable: the triggering
      // user action already recorded its own undo entry.
      if (isResizingRef.current) return
      const state = store.getState()
      if (editorSelectors.editingTextId(state) === item.id) return
      const current = projectDataSelectors
        .mediaItems(state)
        .find(mediaItem => mediaItem.id === item.id)
      if (!current) return
      const dimsChanged = current.width !== width || current.height !== height
      // Rotated text: the engine shifts x/y to keep the pivot fixed as the
      // bitmap grows/shrinks (renderAndApply's rotation compensation) and emits
      // the compensated x/y here. Persist them alongside the size — otherwise
      // the store keeps the pre-compensation coords and the next Redux→engine
      // sync (which pushes the full config) re-applies them, undoing the
      // compensation and drifting the text as it's edited.
      const hasCoords = typeof x === 'number' && typeof y === 'number'
      const coordsChanged =
        hasCoords &&
        (current.coordinates?.x !== x || current.coordinates?.y !== y)
      if (!dimsChanged && !coordsChanged) return
      dispatch(
        projectDataSlice.actions.updateMediaItemWithoutUndoSave({
          id: item.id,
          updates: {
            ...(dimsChanged ? { width, height } : {}),
            ...(coordsChanged ? { coordinates: { x, y } } : {})
          },
          isCheckAssetId: false
        })
      )
    }
    engine.signals.on(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
    return () => {
      engine.signals.off(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
    }
  }, [engine, item.id])

  return { liveSize, liveCoordinates, isResizingRef, isTextSideResizingRef }
}
